import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Radio, Input, Spin, Row, Col, Typography, Image, message } from 'antd';
import { postCall } from '../../utils/network.util';
import { BLOCK_TICKET_URL, CREATE_UPI_BOOKING_URL, GET_UPI_BOOKING_STATUS_URL, GET_UPI_COLLECTION_AMOUNT_URL } from '../../utils/api-url';
import { getDetailsToBookTicket } from '../passenger-form-component/passenger-form.module';
import { isValid, isValidArray } from '../../utils/utilities';
import { roundNumber } from '../../utils/math.utils';
import { addMin, getOrderIdTimestamp, setTimeToDateObject } from '../../utils/date.utils';
import './upi-booking.style.scss';

const { Text } = Typography;

// UPI booking modes - must match constants on the travels server.
const UPI_MODE_QR = 'QR';
const UPI_MODE_LINK = 'LINK';

// The seat is blocked on an 8-minute timer; the same window bounds how long we poll.
const BLOCK_MINUTES = 8;
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = BLOCK_MINUTES * 60 * 1000; // give the customer 8 minutes to pay

/*
  Per-operator travels_app_config flag: when enabled the UPI payment collects the amount
  net of the agent commission (ticket total - commission). The ticket itself is unchanged -
  fare, amount paid and commission are still recorded in full.
*/
export const isUpiAfterCommission = () =>
    isValid(global.travelsAppConfig) && global.travelsAppConfig['UPI_AFTER_COMMISSION'] == 1;

/*
  UPI Booking (Razorpay partner model, no payment-gateway redirect).

  On confirm the flow is:
    1. Block the ticket  -> capture the ticket id from the response.
    2. Generate an order id `operator_code-ticket_id-time_stamp`.
    3. Ask the server to raise the chosen UPI instrument (QR / SMS link)
       against the operator's own Razorpay account.
    4. Poll the server until the payment is collected and the ticket is confirmed.
*/
export const UpiBookingComponent = ({ selectedSchedule, form, discountValue, ticketId, totalCost, onSuccess, disabled = false }) => {
    const [visible, setVisible] = useState(false);
    const [mode, setMode] = useState(UPI_MODE_QR);
    const [mobile, setMobile] = useState('');
    const [stage, setStage] = useState('select'); // 'select' | 'awaiting'
    const [loading, setLoading] = useState(false);
    const [reference, setReference] = useState(null);
    // Exact amount to collect. Equal to the ticket total unless the operator collects
    // post-commission (UPI_AFTER_COMMISSION), in which case only the server can work it
    // out - the browser has no commission data - so it is fetched when the modal opens.
    // Null until resolved, so the gross total is never shown as the amount to collect.
    const [amountToCollect, setAmountToCollect] = useState(isUpiAfterCommission() ? null : totalCost);

    const pollRef = useRef(null);
    const pollTimeoutRef = useRef(null);
    // Ticket id of the seat block raised for this booking. Closing the modal does NOT
    // release it - the block stays until its time_of_release (BLOCK_MINUTES) lapses.
    const blockedTicketIdRef = useRef(null);
    const confirmedRef = useRef(false);

    const clearPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };

    useEffect(() => {
        // Stop polling if the component unmounts mid-flow.
        return () => clearPolling();
    }, []);

    // Per-seat discounted fares excluding GST - the base the server applies commission to.
    const getSeatFares = () => {
        const passengers = form.getFieldValue('passengerDetails');
        if (!isValidArray(passengers) || passengers.length === 0) {
            return [];
        }
        const discountPerSeat = isValid(discountValue) ? discountValue / passengers.length : 0;
        return passengers.map((passenger) => roundNumber((passenger.discountedCostWithoutGst || 0) - discountPerSeat));
    };

    // Resolve the exact amount to collect as soon as the modal opens.
    useEffect(() => {
        if (!visible) {
            return;
        }
        if (!isUpiAfterCommission()) {
            setAmountToCollect(totalCost);
            return;
        }
        setAmountToCollect(null);
        postCall(GET_UPI_COLLECTION_AMOUNT_URL, {
            operatorCode: global.operatorCode,
            tripId: selectedSchedule.trip.id,
            // On-behalf booking: the commission follows the agent the ticket is booked for.
            agentId: form.getFieldValue('agentId') || global.agentId,
            amount: totalCost,
            seatFares: getSeatFares()
        })
            .then((response) => {
                const resolved = response && response.status === 'SUCCESS' && isValid(response.payload)
                    && response.payload.Status === 'SUCCESS' && isValid(response.payload.Payload)
                    ? response.payload.Payload.amount : null;
                // Fall back to the ticket total; the server still charges the correct amount.
                setAmountToCollect(isValid(resolved) ? resolved : totalCost);
            })
            .catch((err) => {
                console.error('Failed to resolve UPI collection amount', err);
                setAmountToCollect(totalCost);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, totalCost]);

    const resetState = () => {
        clearPolling();
        setStage('select');
        setLoading(false);
        setReference(null);
        setMobile('');
        setMode(UPI_MODE_QR);
        blockedTicketIdRef.current = null;
        confirmedRef.current = false;
    };

    const closeModal = () => {
        // Keep an unconfirmed block (it auto-releases on its timer); just refresh the
        // seat layout so the blocked seats show up.
        const refreshLayout = !confirmedRef.current && isValid(blockedTicketIdRef.current);
        resetState();
        setVisible(false);
        if (refreshLayout) {
            onSuccess();
        }
    };

    const blockTicketForUpi = async () => {
        const values = await form.validateFields();
        // Block the seat on an 8-minute timer (time_of_release) so it auto-releases if unpaid.
        // Built like the phone-block release time (IST wall clock) - the server converts it
        // to UTC itself, so passing the zoned moment would shift it 5:30 into the past.
        const blockUntil = addMin(null, BLOCK_MINUTES);
        const releaseDateTime = setTimeToDateObject(blockUntil, blockUntil.format('HH:mm:ss'));
        const payload = getDetailsToBookTicket(values, selectedSchedule, releaseDateTime, false, discountValue, ticketId, false);
        // Flag the block as a UPI booking: the server marks payment_type online and
        // suppresses the block SMS (the customer is notified only after confirmation).
        payload.isUpiBooking = true;
        const response = await postCall(BLOCK_TICKET_URL, payload);
        if (response && response.status === 'SUCCESS' && isValid(response.payload) && response.payload.Status === 'SUCCESS'
            && isValid(response.payload.TicketObject) && isValid(response.payload.TicketObject.ticket)) {
            return {
                ticketId: response.payload.TicketObject.ticket.id,
                customer: {
                    customerName: values.phoneNumber,
                    customerEmail: values.emailId,
                    customerPhone: values.phoneNumber
                }
            };
        }
        const errorMessage = response && isValid(response.payload) && isValid(response.payload.Message)
            ? response.payload.Message
            : 'Failed to block ticket';
        throw new Error(errorMessage);
    };

    const beginPolling = (currentOrderId, currentReference) => {
        const startedAt = Date.now();
        pollRef.current = setInterval(async () => {
            try {
                const response = await postCall(GET_UPI_BOOKING_STATUS_URL, {
                    orderId: currentOrderId,
                    // The server decides the amount actually collected (it may be net of the
                    // agent commission), so echo back what it returned when creating the order.
                    reference: { ...currentReference, amount: isValid(currentReference.amount) ? currentReference.amount : totalCost }
                });
                if (response && response.status === 'SUCCESS' && isValid(response.payload)
                    && response.payload.Status === 'SUCCESS' && isValid(response.payload.Payload)) {
                    const paymentStatus = response.payload.Payload.status;
                    if (paymentStatus === 'Confirmed') {
                        clearPolling();
                        // Ticket is confirmed - closeModal need not refresh the layout (onSuccess below does).
                        confirmedRef.current = true;
                        message.success('Payment received - ticket confirmed!');
                        closeModal();
                        onSuccess();
                    } else if (paymentStatus === 'Pay_Success_Book_Fail') {
                        clearPolling();
                        // Payment was collected - treat as confirmed even if ticket confirmation failed.
                        confirmedRef.current = true;
                        message.error('Payment received but ticket confirmation failed. Please verify the ticket.');
                        setStage('select');
                    }
                    // 'Pending' -> keep polling
                }
            } catch (err) {
                console.error('UPI status poll failed', err);
            }
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                clearPolling();
                message.warning('Payment not completed in time. The seat block will be released if unpaid.');
                setStage('select');
            }
        }, POLL_INTERVAL_MS);
    };

    const startUpiBooking = async () => {
        if (mode === UPI_MODE_LINK && !isValid(mobile)) {
            message.error('Please enter the customer mobile number');
            return;
        }
        setLoading(true);
        try {
            const { ticketId: blockedTicketId, customer } = await blockTicketForUpi();
            const generatedOrderId = `${global.operatorCode}-${blockedTicketId}-${getOrderIdTimestamp()}`;
            blockedTicketIdRef.current = blockedTicketId;
            confirmedRef.current = false;

            const createResponse = await postCall(CREATE_UPI_BOOKING_URL, {
                orderId: generatedOrderId,
                mode,
                amount: totalCost,
                mobile: mode === UPI_MODE_LINK ? mobile : undefined,
                customerName: customer.customerName,
                customerEmail: customer.customerEmail,
                customerPhone: customer.customerPhone
            });

            if (createResponse && createResponse.status === 'SUCCESS' && isValid(createResponse.payload)
                && createResponse.payload.Status === 'SUCCESS' && isValid(createResponse.payload.Payload)) {
                const ref = createResponse.payload.Payload;
                setReference(ref);
                setStage('awaiting');
                setLoading(false);
                beginPolling(generatedOrderId, ref);
            } else {
                const errorMessage = createResponse && isValid(createResponse.payload) && isValid(createResponse.payload.Message)
                    ? createResponse.payload.Message
                    : 'Failed to initiate UPI booking';
                message.error(errorMessage);
                setLoading(false);
            }
        } catch (err) {
            console.error('UPI booking failed', err);
            message.error(isValid(err.message) ? err.message : 'Failed to initiate UPI booking');
            setLoading(false);
        }
    };

    const renderSelect = () => (
        <div>
            <Row className="upi-booking-amount">
                <Col>
                    <Text strong>Amount to collect : </Text>
                    {isValid(amountToCollect) ? amountToCollect : <Spin size="small" className="upi-booking-amount-spin" />}
                </Col>
            </Row>
            <Radio.Group
                className="upi-booking-modes"
                value={mode}
                onChange={(e) => {
                    const newMode = e.target.value;
                    setMode(newMode);
                    // Prefill the mobile number from the passenger form (editable) when
                    // switching to the SMS-link option, without clobbering an edit.
                    // (Note: isValid('') is true in this util, so use a plain emptiness check.)
                    if (newMode === UPI_MODE_LINK && !mobile) {
                        const formPhone = form.getFieldValue('phoneNumber');
                        if (formPhone) {
                            setMobile(formPhone);
                        }
                    }
                }}
            >
                <Radio value={UPI_MODE_QR}>Show QR</Radio>
                <Radio value={UPI_MODE_LINK}>Via mobile number</Radio>
            </Radio.Group>
            {mode === UPI_MODE_LINK &&
                <Row className="upi-booking-input">
                    <Col span={24}>
                        <Input
                            placeholder="Customer mobile number"
                            maxLength={10}
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                        />
                    </Col>
                </Row>
            }
        </div>
    );

    const renderAwaiting = () => {
        // The amount the payment was actually raised for, as returned by the server.
        const raisedAmount = reference && isValid(reference.amount) ? reference.amount : amountToCollect;
        return (
        <div className="upi-booking-awaiting">
            {mode === UPI_MODE_QR && reference && isValid(reference.imageUrl) &&
                <Row justify="center">
                    <Col>
                        <Image src={reference.imageUrl} alt="UPI QR" width={240} preview={false} />
                        <div className="upi-booking-hint">Scan the QR with any UPI app to pay {raisedAmount}</div>
                    </Col>
                </Row>
            }
            {mode === UPI_MODE_LINK &&
                <div className="upi-booking-hint">
                    A UPI payment link for {raisedAmount} has been sent over SMS to <Text strong>{mobile}</Text>.
                    {reference && isValid(reference.shortUrl) && <div><a href={reference.shortUrl} target="_blank" rel="noreferrer">{reference.shortUrl}</a></div>}
                </div>
            }
            <Row justify="center" className="upi-booking-waiting-row">
                <Col>
                    <Spin /> <Text>Waiting for payment confirmation…</Text>
                </Col>
            </Row>
        </div>
        );
    };

    return (
        <div>
            <Button
                type="primary"
                className="upi-booking-btn"
                disabled={disabled}
                onClick={async () => {
                    try {
                        await form.validateFields();
                        resetState();
                        setVisible(true);
                    } catch (err) {
                        // validation errors are surfaced inline by the form
                    }
                }}
            >
                UPI Booking
            </Button>
            <Modal
                title="UPI Booking"
                visible={visible}
                onCancel={closeModal}
                maskClosable={false}
                footer={stage === 'select' ? [
                    <Button key="cancel" className="exit-btn" onClick={closeModal}>Exit</Button>,
                    <Button key="ok" type="primary" loading={loading} onClick={startUpiBooking}>Proceed</Button>
                ] : [
                    <Button key="close" className="exit-btn" onClick={closeModal}>Close</Button>
                ]}
            >
                {stage === 'select' ? renderSelect() : renderAwaiting()}
            </Modal>
        </div>
    );
};
